import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, XCircle, Eye, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function calcAge(dob) {
  if (!dob) return '';
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : '';
}

function fmtDT(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const DECL = [
  'I confirm that the above filled information is true to the best of my knowledge.',
  'I agree that the teacher will always guide me to the best of her ability and any injury due to exercises/asanas during class will not be the responsibility of the teacher.',
  'I agree that the fees once paid is non-refundable.',
];

const INPUT = 'bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm';

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[10001] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

function StatusBadge({ submitted }) {
  return submitted
    ? <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 rounded-full px-3 py-1 text-xs font-heading font-medium"><CheckCircle className="w-3 h-3" strokeWidth={2} />Submitted</span>
    : <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-600 rounded-full px-3 py-1 text-xs font-heading font-medium"><XCircle className="w-3 h-3" strokeWidth={2} />Pending</span>;
}


export default function AdminProfileDetails() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  const fetchAll = async () => {
    try {
      const [rowRes, batRes] = await Promise.all([
        axios.get(`${API}/api/admin/profile-details`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() }),
      ]);
      setRows(rowRes.data);
      setBatches(batRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = rows
    .filter(r => !showArchived ? !r.student.is_archived : r.student.is_archived)
    .filter(r => !search || r.student.name.toLowerCase().includes(search.toLowerCase()))
    .filter(r => !batchFilter || r.student.batch_id === batchFilter)
    .filter(r => {
      if (statusFilter === 'submitted') return !!r.profile;
      if (statusFilter === 'pending') return !r.profile;
      return true;
    });

  const submitted = rows.filter(r => !!r.profile).length;
  const pending = rows.filter(r => !r.profile && !r.student.is_archived).length;

  return (
    <div className="space-y-5 animate-fade-in-up">

      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Admin</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Student Profile Details</h1>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3">
        <span className="text-xs font-body bg-green-50 text-green-700 rounded-full px-3 py-1.5 font-medium">Submitted: {submitted}</span>
        <span className="text-xs font-body bg-red-50 text-red-600 rounded-full px-3 py-1.5 font-medium">Pending: {pending}</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
          <input type="text" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-white border-2 border-[#E7E5E4] rounded-2xl pl-10 pr-4 py-2.5 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm" />
        </div>
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}
          className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-2.5 focus:border-[#F59E0B] outline-none text-[#292524] font-body text-sm min-w-36">
          <option value="">All Batches</option>
          {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-2.5 focus:border-[#F59E0B] outline-none text-[#292524] font-body text-sm min-w-36">
          <option value="all">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Active/Archived tabs */}
      <div className="flex items-center gap-2 bg-white rounded-2xl p-1.5 shadow-card border border-[#E7E5E4]/50 w-fit">
        <button onClick={() => setShowArchived(false)}
          className={`px-5 py-2 rounded-xl text-sm font-heading font-medium transition-all ${!showArchived ? 'bg-[#F59E0B] text-white' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          Active
        </button>
        <button onClick={() => setShowArchived(true)}
          className={`px-5 py-2 rounded-xl text-sm font-heading font-medium transition-all ${showArchived ? 'bg-[#57534E] text-white' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          Archived
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
          <p className="font-heading font-medium text-[#292524]">No students match your filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFFAF0] border-b border-[#E7E5E4]/60">
                <tr>
                  {['Student Name','Batch','Profile Status','Joining Date',''].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-[#78716C] uppercase tracking-wider px-5 py-3.5 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E7E5E4]/40">
                {filtered.map(row => (
                  <tr key={row.student.id} className="hover:bg-[#FFFAF0]/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-xs shrink-0">
                          {row.student.name?.[0]?.toUpperCase()}
                        </div>
                        <span className="font-body text-sm text-[#292524] font-medium">{row.student.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-[#57534E] font-body">{row.batch_name || '—'}</td>
                    <td className="px-5 py-4"><StatusBadge submitted={!!row.profile} /></td>
                    <td className="px-5 py-4 text-sm text-[#57534E] font-body">{row.profile?.joining_date || '—'}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => navigate(`/admin/profiles/${row.student.id}`)}
                        className="flex items-center gap-1.5 text-xs text-[#F59E0B] hover:text-[#D97706] font-heading font-medium transition-colors">
                        <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-[#E7E5E4]/40">
            {filtered.map(row => (
              <div key={row.student.id} className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-sm shrink-0">
                    {row.student.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-medium text-[#292524] text-sm truncate">{row.student.name}</p>
                    <p className="text-xs text-[#A8A29E] font-body">{row.batch_name || 'No batch'}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <StatusBadge submitted={!!row.profile} />
                  <button onClick={() => navigate(`/admin/profiles/${row.student.id}`)} className="text-xs text-[#F59E0B] font-heading font-medium">View →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
